'use client'

import type { MouseEvent, ReactNode, SyntheticEvent } from 'react'
import { SharedSurfaceProvider, type SharedSkin, type SharedSurface } from '@/deskUiSkeletons/Shared/SharedSurfaceContext'
import { PanelsSurfaceProvider, type PanelsSkin, type PanelsSurface } from '@/deskUiSkeletons/Page/PanelsSurfaceContext'
import { RowSurfaceProvider } from '@/deskUiSkeletons/rowTemplates/RowSurfaceContext'
import type { RowSkin, RowSurface, RowVariantDefinition, SubTemplateProps } from '@/deskUiSkeletons/rowTemplates/RowTemplateTypes'
import { defineRowVariantRegistry } from '@/deskUiSkeletons/rowTemplates/RowVariantRegistry'
import { SubRow } from '@/deskUiSkeletons/subs/SubRow'
import { MoreLink } from '@/deskUiSkeletons/Shared/Shared'
import { showQuickReplyModal } from '@/deskUiSkeletons/Shared/QuickReplyModal'
import { legacyContractDataAttrs, legacyHookAttrs, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchSubsDisclosure } from '@/msgdeskRuntime/subsDisclosure'
import { createPagingModel, dispatchSubsNavigation, type PagingTarget } from '@/msgdeskRuntime/subsNavigation'
import { dispatchSubsSelection } from '@/msgdeskRuntime/subsSelection'
import { selectCheckedSubIds } from '@/msgdeskRuntime/state'
import type { SubAttachmentState, SubsSection, SubRowVariant } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { sharedChatAnswerVariant } from '@/msgComponents/rowVariants/Chat/Chat'
import { defaultBlockedPreviewSkin } from '@/msgComponents/skins/blockedPreviewSkin'
import { isAudioAttachment, isImageAttachment } from '@/contracts/mediaAttachments'
import { productRowVariantForPath, productRowVariantsForPath } from '../../productDisplayCatalog'
import { MusicLoading } from './MusicLoading'

export type MusicSurfaceKind = 'musicbox' | 'musictrack'
export type MusicCreationIntent = 'comment' | 'track' | 'box' | null

const sharedSkin: SharedSkin = {
  navItem: 'flex h-9 cursor-pointer items-center rounded-full px-3 text-xs font-medium text-[#6d6259] hover:bg-[#efe4d5] hover:text-[#3b2417]',
  navItemSelected: 'bg-[#6b3f24] text-white',
  navLabel: 'flex items-center',
  navLoading: 'animate-pulse',
  moreLink: 'mt-3 inline-flex w-max rounded-full bg-[#efe4d5] px-3 py-2 text-xs font-semibold text-[#6b3f24] hover:bg-[#e4d1ba]',
  testBadge: 'fixed right-2 top-16 z-[100000] rounded bg-red-600 px-2 py-1 text-[10px] font-bold text-white',
  testBadgeInfo: 'hidden',
  dialogBackdrop: 'hidden', dialogWindow: 'hidden',
  savingBackdrop: 'fixed inset-0 z-[100001] hidden bg-black/25',
  savingContent: 'fixed left-1/2 top-1/2 z-[100002] hidden -translate-x-1/2 -translate-y-1/2',
  msgBoxOverlay: 'fixed inset-0 z-[100003] hidden bg-white/75 text-center text-[#2f241d]',
  msgBoxSpacer: 'h-[40%]',
  msgBox: 'inline-flex max-w-full flex-col overflow-hidden rounded-2xl border border-[#d9c8b7] bg-white shadow-xl',
  msgBoxInfo: 'flex flex-col items-center p-4', msgBoxText: 'my-2', msgBoxPin: 'hidden',
  msgBoxCaptchaImg: 'block h-auto w-auto',
  msgBoxButtons: 'flex justify-center gap-2 border-t border-[#e8ded3] bg-[#faf7f2] p-2',
  msgBoxClose: 'cursor-pointer px-3 py-1 text-[#6b3f24]', msgBoxHiddenCancel: 'hidden',
  qrOverlay: 'fixed inset-0 z-[100000] flex items-center justify-center bg-black/30 p-4',
  qrInline: 'my-2 mx-2 w-full max-w-[560px]', qrRoot: 'w-[min(523px,calc(100vw-32px))]',
  qrForm: 'block min-h-[180px] rounded-2xl bg-white p-3 text-[#2f241d] shadow-xl', qrEditForm: 'w-full',
  qrTextarea: 'block h-[130px] w-full rounded-xl border border-[#d9c8b7] bg-white p-2 text-sm',
  qrEmailLabel: 'block text-xs', qrCheckbox: 'mr-2', qrEmailLink: 'text-xs text-[#6b3f24]', qrEmailValue: 'underline',
  qrActions: 'flex justify-end gap-2', qrSaveButton: 'rounded-full bg-[#6b3f24] px-3 py-1.5 text-xs font-semibold text-white',
  qrShadowRight: 'hidden', qrShadowBottom: 'hidden',
  pinOverlay: 'fixed inset-0 z-[100004] flex items-center justify-center bg-white/75 p-5 text-xs text-[#2f241d] backdrop-blur-sm',
  pinBox: 'flex w-[280px] max-w-full flex-col rounded-2xl border border-[#d9c8b7] bg-white shadow-2xl',
  pinMessage: 'px-4 pb-2 pt-4 text-center font-semibold',
  pinInput: 'mx-4 mb-3 rounded-xl border border-[#d9c8b7] bg-white p-2 text-sm outline-none',
  pinActions: 'flex justify-end gap-2 border-t border-[#e8ded3] bg-[#faf7f2] p-3',
  pinButton: 'rounded-full px-3 py-1.5 text-xs text-[#6d6259] hover:bg-[#efe4d5]',
  pinButtonPrimary: 'rounded-full bg-[#6b3f24] px-3 py-1.5 text-xs font-semibold text-white',
}

const sharedSurface: SharedSurface = { Loading: MusicLoading, skin: sharedSkin }

function stop(event: SyntheticEvent) { event.preventDefault(); event.stopPropagation() }

function MusicFoldSubsButton({ section = 0, label, count }: { section?: 0 | 1; label: string; count?: number }) {
  const contract: LegacyContractName = section === 0 ? 'subs.menuToggle.answers' : 'subs.menuToggle.submsges'
  return (
    <a href="#" {...legacyHookAttrs(contract, 'inline-flex w-max items-center gap-1 rounded-full bg-[#efe4d5] px-3 py-1.5 text-xs font-semibold text-[#6b3f24] no-underline')} onClick={event => { stop(event); dispatchSubsDisclosure({ action: 'toggle-section', section, target: event.currentTarget }) }}>
      {label}{typeof count === 'number' ? <span className="text-[#9a8778]">({count})</span> : null}
    </a>
  )
}

function MusicPagingBar({ section, currentPage = 1, totalPages = 1, registerContract = true }: { section: 0 | 1; currentPage?: number; totalPages?: number; registerContract?: boolean }) {
  const paging = createPagingModel(currentPage, totalPages)
  const contract = section === 0 ? 'subs.paging.answers' : 'subs.paging.submsges'
  const go = (event: MouseEvent<HTMLElement>, page: PagingTarget) => { stop(event); dispatchSubsNavigation({ action: 'set-page', section, page, target: event.currentTarget }) }
  return (
    <div {...(registerContract ? legacyHookAttrs(contract, 'inline-flex items-center gap-1 rounded-full bg-[#efe4d5] p-1') : { className: 'inline-flex items-center gap-1 rounded-full bg-[#efe4d5] p-1' })}>
      {paging.tokens.map((token, index) => token === 'gap' ? <span key={`gap-${index}`} className="px-1">…</span> : <a key={token} href="#" className={`flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-xs ${token === paging.currentPage ? 'bg-[#6b3f24] text-white' : 'text-[#6b3f24]'}`} onClick={event => go(event, token)}>{token}</a>)}
      <span className="inline-flex">
        <a href="#" style={paging.previousDisabled ? { display: 'none' } : undefined} className="px-2" onClick={event => go(event, 'previous')}>‹</a>
        <a href="#" style={paging.nextDisabled ? { display: 'none' } : undefined} className="px-2" onClick={event => go(event, 'next')}>›</a>
      </span>
    </div>
  )
}

function MusicRefreshSubs({ section }: { section: 0 | 1 }) {
  return <button type="button" className="h-8 w-8 rounded-full bg-[#efe4d5] text-[#6b3f24]" onClick={event => dispatchSubsNavigation({ action: 'refresh', section, target: event.currentTarget })}>↻</button>
}

function MusicCheckedsInfo({ section, mode = 'msg' }: { section: 0 | 1; mode?: 'msg' | 'drafts' | 'trash' | 'favs'; visual?: 'modern' | 'legacy' }) {
  const { state } = useSubsStore()
  const sectionKey = section === 0 ? 'answers' : 'submsges'
  const checkedMode = state.renderProjection.mode === 'subs' ? sectionKey : (mode === 'msg' ? state.renderProjection.mode : mode)
  const checked = selectCheckedSubIds(state, sectionKey, checkedMode)
  const contract = section === 0 ? 'subs.checked.answers' : 'subs.checked.submsges'
  return <div {...legacyContractDataAttrs(contract)} className="inline-flex min-h-8 items-center text-xs"><button type="button" className="rounded-full bg-[#efe4d5] px-3 py-1.5 text-[#6b3f24]" onClick={event => dispatchSubsSelection({ action: 'toggle-visible', section, target: event.currentTarget })}>Select{checked.length ? ` (${checked.length})` : ''}</button></div>
}

const panelsSkin: PanelsSkin = {
  toolbar: 'mb-3 flex min-h-4 items-center', toolbarHide: 'rounded-full bg-[#efe4d5] px-3 py-1.5 text-xs text-[#6b3f24]', toolbarMessage: 'hidden',
  panelAnswers: 'relative mb-7 flex flex-col rounded-2xl border border-[#e1d5c8] bg-white p-4 shadow-sm',
  panelSubmsges: 'relative mb-7 flex flex-col rounded-2xl border border-[#e1d5c8] bg-white p-4 shadow-sm',
  panelTools: 'mb-3 flex min-h-8 flex-wrap items-center gap-2', rootList: 'flex list-none flex-col gap-3 p-0 text-sm',
  sectionInfo: 'mb-3 inline-flex w-max rounded-full bg-[#efe4d5] px-3 py-1.5 text-xs font-semibold text-[#6b3f24]', sectionInfoCount: 'ml-1',
  menuPanel: 'relative flex-col rounded-2xl border border-[#e1d5c8] bg-white p-4 shadow-sm', menuPanelShown: '!flex', menuPanelHidden: '!hidden', togglePhoto: 'hidden',
  menuPanelTools: 'mb-3 flex min-h-8 flex-wrap items-center gap-2', menuList: 'flex list-none flex-col gap-3 p-0 text-sm',
  sectionSwitch: 'mb-3 flex w-max gap-1 rounded-full bg-[#efe4d5] p-1 text-xs', sectionSwitchButton: 'rounded-full px-3 py-2',
  sectionSwitchButtonActive: 'bg-[#6b3f24] text-white', sectionSwitchButtonInactive: 'text-[#6b3f24]', menuEmpty: 'px-2 py-4 text-xs text-[#9a8778]',
}

function mainTemplateVariant(_section: SubsSection): SubRowVariant { return 'auto' }
function menuTemplateVariant(_mode: 'drafts' | 'trash' | 'favs', _section: SubsSection): SubRowVariant { return 'folder' }

const panelsSurface: PanelsSurface = {
  FoldSubsButton: MusicFoldSubsButton, PagingBar: MusicPagingBar, RefreshSubs: MusicRefreshSubs,
  CheckedsInfo: MusicCheckedsInfo, MoreLink, menuEmptyLabel: 'No music items',
  config: { mainTemplateVariant, menuTemplateVariant }, skin: panelsSkin,
}

function MusicCommentTemplate({ display, controls, slots }: SubTemplateProps) {
  return <article className="rounded-2xl border border-[#e1d5c8] bg-[#fffdf9] p-4"><header className="flex justify-between gap-3"><strong>{display.identity.author || display.title || 'Anonymous'}</strong>{controls.content.time}</header><div className="mt-2">{controls.content.text}</div><footer className="mt-3 flex gap-2">{controls.actions.quickReply}{controls.actions.favorite}{controls.links.answers}</footer>{slots.allChildren ? <div className="ml-5 mt-3 border-l border-[#e1d5c8] pl-4">{slots.allChildren}</div> : null}</article>
}

function MusicRowImages({ images }: { images: SubAttachmentState[] }) {
  if (!images.length) return null
  return (
    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3" data-md-music-row-images>
      {images.map((image, index) => (
        <a key={`${image.id}-${index}`} className="overflow-hidden rounded-xl border border-[#e1d5c8] bg-[#f7f4ee]" href={image.src} aria-label={`Open ${image.title || image.filename || `image ${index + 1}`}`}>
          <img className="aspect-square h-full w-full object-cover" src={image.thumbSrc || image.src} alt={image.title || image.filename || `Music image ${index + 1}`} />
        </a>
      ))}
    </div>
  )
}

function MusicTrackTemplate({ display, controls, slots }: SubTemplateProps) {
  const media = display.permissions.canViewContent && !display.access.isContentLocked ? display.media.attachments : []
  const audio = media.filter(attachment => attachment.mediaKind === 'audio' || isAudioAttachment(attachment.filename || attachment.src))
  const images = media.filter(attachment => attachment.mediaKind === 'image' || isImageAttachment(attachment.filename || attachment.src))
  return (
    <article className="rounded-2xl border border-[#d8c4b0] bg-white p-4 shadow-sm" data-md-sub-template="music-track">
      <header className="flex min-w-0 items-start justify-between gap-3"><div className="min-w-0 text-lg font-semibold text-[#2f241d]">{controls.content.title}</div>{controls.content.time}</header>
      <div className="mt-2 text-sm text-[#6d6259]">{controls.content.text}</div>
      <MusicRowImages images={images} />
      {audio.length ? <div className="mt-3 space-y-2">{audio.map((attachment, index) => <div key={`${attachment.id}-${index}`}><div className="mb-1 truncate text-xs font-medium text-[#6b3f24]">{attachment.title || attachment.filename || `Track ${index + 1}`}</div><audio className="h-10 w-full" controls preload="metadata" src={attachment.src}>{attachment.mime ? <source src={attachment.src} type={attachment.mime} /> : null}</audio></div>)}</div> : <p className="mt-3 text-xs text-[#9a8778]">No playable audio attachments.</p>}
      <footer className="mt-3 flex flex-wrap items-center gap-2">
        {display.href ? <a className="rounded-full bg-[#efe4d5] px-3 py-1.5 text-xs font-semibold text-[#6b3f24] no-underline hover:bg-[#e4d1ba]" href={display.href}>Open track</a> : null}
        {controls.actions.check}{controls.actions.favorite}{controls.links.answers}
      </footer>
      {slots.allChildren}
    </article>
  )
}

function MusicBoxTemplate({ display, controls, slots }: SubTemplateProps) {
  const images = display.permissions.canViewContent && !display.access.isContentLocked
    ? display.media.attachments.filter(attachment => attachment.mediaKind === 'image' || isImageAttachment(attachment.filename || attachment.src))
    : []
  return (
    <article className="overflow-hidden rounded-2xl border border-[#d8c4b0] bg-white shadow-sm" data-md-sub-template="music-box">
      <header className="bg-gradient-to-br from-[#2b1d16] to-[#6b3f24] px-4 py-3 text-[#fffaf3]">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0 text-lg font-semibold">{controls.content.title}</div>
          <span className="text-[#d9c9bb]">{controls.content.time}</span>
        </div>
        <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#d7b894]">Music Box</p>
      </header>
      <div className="p-4">
        <div className="text-sm text-[#6d6259]">{controls.content.text}</div>
        <MusicRowImages images={images} />
        <footer className="mt-3 flex flex-wrap gap-2">{controls.actions.check}{controls.actions.favorite}{controls.links.answers}{controls.links.submsges}</footer>
        {slots.allChildren}
      </div>
    </article>
  )
}

function MusicAnswerTemplate(props: SubTemplateProps) {
  const kind = String(props.display.kind || '').toLowerCase()
  if (kind === 'music_track' || kind === 'musictrack') return <MusicTrackTemplate {...props} />
  if (kind === 'music_box' || kind === 'musicbox') return <MusicBoxTemplate {...props} />
  return <MusicCommentTemplate {...props} />
}

const rowSkin: RowSkin = {
  row: { base: 'min-w-0 list-none text-[#2f241d]', hidden: 'hidden', checked: 'opacity-70', foreign: 'opacity-90', foreignFrom: 'block text-xs text-[#85776c]' },
  blockedNotice: { ...defaultBlockedPreviewSkin },
  icon: { base: 'inline-flex h-7 w-7 items-center justify-center rounded-full border', checkOn: 'border-[#6b3f24] bg-[#6b3f24]', checkOff: 'border-[#d8c4b0] bg-white', favOn: 'border-amber-400 bg-amber-300', favOff: 'border-[#d8c4b0] bg-white', qrOn: 'border-[#6b3f24] bg-[#efe4d5]', qrOff: 'border-[#d8c4b0] bg-white' },
  titleLink: 'font-semibold text-[#2f241d] no-underline hover:text-[#6b3f24]', timeWrap: 'text-xs text-[#85776c]', timeText: 'text-xs text-[#85776c]',
  text: 'min-w-0 whitespace-normal text-sm leading-6 text-[#5b5048]', metaLink: 'text-xs font-medium text-[#6b3f24]', metaMore: 'text-xs font-medium text-[#6b3f24]', meta: 'text-xs text-[#85776c]',
  feed: 'mt-3 flex list-none flex-col gap-3 p-0',
  folder: { row: 'flex flex-col gap-1 rounded-xl border border-[#e1d5c8] bg-white px-3 py-2', head: 'flex min-w-0 items-center gap-2', title: 'min-w-0 flex-1 truncate text-sm', time: 'text-xs text-[#85776c]', text: 'truncate text-sm text-[#6d6259]', counts: 'flex gap-3 text-xs text-[#85776c]', count: 'whitespace-nowrap' },
}

function registry(kind: MusicSurfaceKind) {
  const answers = { ...productRowVariantForPath(kind, 2), Template: MusicAnswerTemplate } as RowVariantDefinition
  if (kind === 'musictrack') {
    const rows = productRowVariantsForPath(kind)
    return defineRowVariantRegistry({ answers: { variants: [answers, sharedChatAnswerVariant], fallback: rows.answers.fallback }, submsges: { variants: [answers], fallback: rows.submsges.fallback } })
  }
  const box = { ...productRowVariantForPath('musicbox', 3, 'submsges'), Template: MusicBoxTemplate } as RowVariantDefinition
  const rows = productRowVariantsForPath('musicbox')
  return defineRowVariantRegistry({ answers: { variants: [answers, sharedChatAnswerVariant], fallback: rows.answers.fallback }, submsges: { variants: [box], fallback: rows.submsges.fallback } })
}

export function MusicProviders({ kind, children }: { kind: MusicSurfaceKind; children: ReactNode }) {
  const rowSurface: RowSurface = { SubRow, showQuickReplyModal, variants: registry(kind), config: { nestedTemplateVariant: () => 'auto' }, skin: rowSkin }
  return <SharedSurfaceProvider value={sharedSurface}><RowSurfaceProvider value={rowSurface}><PanelsSurfaceProvider value={panelsSurface}>{children}</PanelsSurfaceProvider></RowSurfaceProvider></SharedSurfaceProvider>
}
